import React, { useState, useRef, useEffect } from 'react';
import {
  AppBar, Toolbar, Button, IconButton, Badge,
  BottomNavigation, BottomNavigationAction, Box, Divider, List,
  ListItem, ListItemText, ListItemIcon, CircularProgress,
  Paper, ClickAwayListener, Popper, Typography, Switch,
  FormControlLabel, Snackbar, Alert, useMediaQuery, Avatar, Tooltip
} from '@mui/material';
import {
  ShoppingCart, AccountCircle, Home, Wallet, ShoppingBag, Dashboard,
  AdminPanelSettings, Notifications as NotificationsIcon,
  NotificationsNone, DoneAll, Close, Logout, VolumeUp, VolumeOff, HeadsetMic,
  Share as ShareIcon,
  Storefront as StorefrontIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useNotifications } from '../context/NotificationContext';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import { useTheme } from '@mui/material/styles';

const NavBar = ({ keywordsData, language, toggleLanguage }) => {
  const { cartCount, setCartOpen } = useCart();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const isRtl = theme.direction === 'rtl';
  const { user, logout } = useAuth();
  const {
    notifications,
    unreadCount,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    loading,
    soundEnabled,
    toggleSound
  } = useNotifications();

  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [pharmacyStatus, setPharmacyStatus] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const notificationRef = useRef(null);

  const getCurrentNavigationValue = () => {
    const path = location.pathname;
    const restaurantId = localStorage.getItem('current_rest_id_now') || 0;
    if (path === '/' || path === '/home') return 'home';
    if (path === '/account') return 'account';
    if (path === '/wallet') return 'wallet';
    if (path === '/admin') return 'admin';
    if (path === '/super') return 'super';
    if (path === '/about') return 'about';
    if (path === `/restaurant/${parseInt(restaurantId)}`) return `restaurant/${parseInt(restaurantId)}`;
    if (path === '/casher/orders') return 'casher/orders';
    if (path === '/admin/chats') return 'admin/chats';
    if (path === '/my-orders') return 'my-orders';
    if (path === '/super/chats') return 'super/chats';
    return '';
  };

  const [value, setValue] = useState(getCurrentNavigationValue());

  useEffect(() => {
    setValue(getCurrentNavigationValue());
  }, [location.pathname]);

  const getBottomNavItems = () => {
    const restaurantId = localStorage.getItem('current_rest_id_now');
    const baseItems = [];

    const hasValidRestaurantId = restaurantId && restaurantId !== '0' && restaurantId !== 0;
    const isNotAdminOrSuper = user?.role !== 'admin' && user?.role !== 'super';

    if (hasValidRestaurantId && isNotAdminOrSuper) {
      baseItems.push({
        label: language === 'ar' ? 'المنيو' : 'Menu',
        value: `restaurant/${parseInt(restaurantId)}`,
        icon: <StorefrontIcon />
      });
    }

    baseItems.push(
      { label: language === 'ar' ? keywordsData.home_txt_ar : keywordsData.home_txt_en, value: 'home', icon: <Home /> },
      { label: language === 'ar' ? keywordsData.account_txt_ar : keywordsData.account_txt_en, value: 'account', icon: <AccountCircle /> },
      { label: language === 'ar' ? keywordsData.wallet_txt_ar : keywordsData.wallet_txt_en, value: 'wallet', icon: <Wallet /> },
    );

    const roleBasedItems = {
      super: [
        ...baseItems,
        { label: language === 'ar' ? 'المحادثات' : 'Chats', value: 'super/chats', icon: <HeadsetMic /> },
        { label: language === 'ar' ? keywordsData.dashboard_txt_ar : keywordsData.dashboard_txt_en, value: 'super', icon: <Dashboard /> },
      ],
      admin: [
        ...baseItems,
        { label: language === 'ar' ? 'المحادثات' : 'Chats', value: 'admin/chats', icon: <HeadsetMic /> },
        { label: language === 'ar' ? keywordsData.dashboard_txt_ar : keywordsData.dashboard_txt_en, value: 'admin', icon: <Dashboard /> },
      ],
      casher: [
        ...baseItems,
        { label: language === 'ar' ? 'الطلبات' : 'Orders', value: 'casher/orders', icon: <ShoppingBag /> },
      ],
      user: [
        ...baseItems,
        { label: language === 'ar' ? 'طلباتي' : 'My Orders', value: 'my-orders', icon: <ShoppingBag /> },
      ],
    };

    return roleBasedItems[user?.role] || roleBasedItems.user;
  };

  useEffect(() => {
    const fetchPharmacyStatus = async () => {
      try {
        const response = await api.get('/categories/pharm_status');
        setPharmacyStatus(response.data.value === '1');
      } catch (error) {
        console.error('Error fetching pharmacy status:', error);
        const savedStatus = localStorage.getItem('pharmacyStatus');
        if (savedStatus !== null) {
          setPharmacyStatus(savedStatus === 'true');
        }
      }
    };
    if (user?.role === 'admin') fetchPharmacyStatus();
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNotificationClick = (event) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
    fetchNotifications();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const openStatusTxt = language === 'ar' ? keywordsData.open_caffe_status_txt_ar : keywordsData.open_caffe_status_txt_en;
  const closedStatusTxt = language === 'ar' ? keywordsData.closed_caffe_status_txt_ar : keywordsData.closed_caffe_status_txt_en;
  const openTxt = language === 'ar' ? keywordsData.open_status_txt_ar : keywordsData.open_status_txt_en;
  const closedTxt = language === 'ar' ? keywordsData.closed_status_txt_ar : keywordsData.closed_status_txt_en;

  const handleStatusChange = async (event) => {
    const newStatus = event.target.checked;
    setLoadingStatus(true);

    try {
      await api.put('/admin/pharm_status', {
        key: 'pharm_open_status',
        value: newStatus ? '1' : '0'
      });

      setPharmacyStatus(newStatus);
      localStorage.setItem('pharmacyStatus', newStatus);

      setSnackbar({
        open: true,
        message: newStatus ? openStatusTxt : closedStatusTxt,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating pharmacy status:', error);
      setSnackbar({
        open: true,
        message: 'فشل في تحديث حالة المتجر',
        severity: 'error'
      });
      setPharmacyStatus(!newStatus);
    } finally {
      setLoadingStatus(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const [isCopied, setIsCopied] = useState(false);
  const currentUrl = window.location.href;
  const siteTitle = document.title;

  const shareWithWebAPI = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: siteTitle,
          text: `تابع أحدث العروض على ${siteTitle}`,
          url: currentUrl,
        });
      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else {
      copyToClipboard();
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(currentUrl).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    }).catch(err => {
      console.error('Failed to copy: ', err);
      const textArea = document.createElement('textarea');
      textArea.value = currentUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    });
  };

  const open = Boolean(anchorEl);
  const id = open ? 'notification-popper' : undefined;

  // Shared styles
  const goldAccent = '#E6B800';
  const softGold = 'rgba(230, 184, 0, 0.12)';
  const surfaceBg = 'rgba(18, 18, 18, 0.85)';

  const iconBtnSx = {
    color: '#EDEDED',
    width: 40,
    height: 40,
    transition: 'all 0.2s ease',
    '&:hover': {
      color: goldAccent,
      backgroundColor: softGold,
      transform: 'translateY(-1px)',
    },
  };

  return (
    <>
      {/* App Bar */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          top: 0,
          background: surfaceBg,
          backdropFilter: 'saturate(180%) blur(16px)',
          WebkitBackdropFilter: 'saturate(180%) blur(16px)',
          borderBottom: '1px solid rgba(230, 184, 0, 0.18)',
          boxShadow: '0 1px 0 rgba(255,255,255,0.02), 0 8px 24px rgba(0,0,0,0.35)',
          zIndex: 1000,
          color: '#fff',
        }}
      >
        <Toolbar
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            minHeight: { xs: '60px', sm: '68px', md: '76px' },
            px: { xs: 1.5, sm: 2.5, md: 4 },
            gap: 1,
          }}
        >
          {/* Logo */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              height: { xs: 42, sm: 50, md: 56 },
              width: { xs: 42, sm: 50, md: 56 },
              borderRadius: '14px',
              transition: 'transform 0.25s ease, box-shadow 0.25s ease',
              '&:hover': {
                transform: 'translateY(-2px) scale(1.03)',
                boxShadow: '0 8px 24px rgba(230, 184, 0, 0.25)',
              },
            }}
            onClick={() => navigate('/')}
          >
            <svg width="100%" height="100%" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="goldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#FFD700" />
                  <stop offset="100%" stopColor="#E6A800" />
                </linearGradient>
                <linearGradient id="darkGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#1F1F1F" />
                  <stop offset="100%" stopColor="#0D0D0D" />
                </linearGradient>
                <filter id="shadow">
                  <feDropShadow dx="0" dy="1" stdDeviation="2" floodColor="#000" floodOpacity="0.5" />
                </filter>
              </defs>

              <circle cx="60" cy="60" r="55" fill="url(#darkGradient)" stroke="url(#goldGradient)" strokeWidth="3" />
              <circle cx="60" cy="60" r="50" fill="none" stroke="#FFD700" strokeWidth="0.8" strokeDasharray="3 5" opacity="0.2" />

              <ellipse cx="60" cy="62" rx="32" ry="8" fill="url(#goldGradient)" stroke="#FFD700" strokeWidth="1.5" filter="url(#shadow)" />
              <rect x="56" y="68" width="8" height="16" fill="url(#goldGradient)" rx="2" />
              <ellipse cx="60" cy="86" rx="20" ry="5" fill="url(#goldGradient)" stroke="#FFD700" strokeWidth="1" />
              <ellipse cx="60" cy="86" rx="16" ry="3.5" fill="#0D0D0D" />

              <g transform="translate(60, 52) rotate(-8)">
                <rect x="-18" y="-20" width="36" height="36" rx="3" fill="#FFD700" filter="url(#shadow)" />
                <rect x="-15" y="-17" width="7" height="7" fill="#0D0D0D" rx="1" />
                <rect x="-13" y="-15" width="3" height="3" fill="#FFD700" />
                <rect x="8" y="-17" width="7" height="7" fill="#0D0D0D" rx="1" />
                <rect x="10" y="-15" width="3" height="3" fill="#FFD700" />
                <rect x="-15" y="8" width="7" height="7" fill="#0D0D0D" rx="1" />
                <rect x="-13" y="10" width="3" height="3" fill="#FFD700" />
                <rect x="-6" y="-17" width="3" height="3" fill="#0D0D0D" />
                <rect x="-1" y="-14" width="3" height="3" fill="#0D0D0D" />
                <rect x="4" y="-17" width="3" height="3" fill="#0D0D0D" />
                <rect x="-6" y="-11" width="3" height="3" fill="#0D0D0D" />
                <rect x="0" y="-8" width="3" height="3" fill="#0D0D0D" />
                <rect x="5" y="-11" width="3" height="3" fill="#0D0D0D" />
                <rect x="-15" y="-6" width="3" height="3" fill="#0D0D0D" />
                <rect x="-10" y="-3" width="3" height="3" fill="#0D0D0D" />
                <rect x="-4" y="-2" width="3" height="3" fill="#0D0D0D" />
                <rect x="1" y="-4" width="3" height="3" fill="#0D0D0D" />
                <rect x="7" y="-6" width="3" height="3" fill="#0D0D0D" />
                <rect x="12" y="-3" width="3" height="3" fill="#0D0D0D" />
                <rect x="-15" y="1" width="3" height="3" fill="#0D0D0D" />
                <rect x="-10" y="4" width="3" height="3" fill="#0D0D0D" />
                <rect x="-4" y="2" width="3" height="3" fill="#0D0D0D" />
                <rect x="1" y="5" width="3" height="3" fill="#0D0D0D" />
                <rect x="7" y="3" width="3" height="3" fill="#0D0D0D" />
                <rect x="12" y="6" width="3" height="3" fill="#0D0D0D" />
                <rect x="-10" y="9" width="3" height="3" fill="#0D0D0D" />
                <rect x="-4" y="11" width="3" height="3" fill="#0D0D0D" />
                <rect x="1" y="9" width="3" height="3" fill="#0D0D0D" />
                <rect x="7" y="11" width="3" height="3" fill="#0D0D0D" />
                <rect x="12" y="9" width="3" height="3" fill="#0D0D0D" />
              </g>

              <text x="42" y="108" textAnchor="middle" fontSize="22" fontWeight="900" fill="#FFD700" fontFamily="Arial, Helvetica, sans-serif" letterSpacing="0.5">QR</text>
              <text x="78" y="108" textAnchor="middle" fontSize="20" fontWeight="400" fill="#FFFFFF" fontFamily="Arial, Helvetica, sans-serif" letterSpacing="0.5">table</text>
              <line x1="30" y1="114" x2="90" y2="114" stroke="#FFD700" strokeWidth="1" opacity="0.5" />
              <circle cx="60" cy="114" r="1.5" fill="#FFD700" />
            </svg>
          </Box>

          {/* Right Side Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.25, sm: 0.5, md: 1 } }}>
            {/* Cart */}
            <Tooltip title={language === 'ar' ? 'السلة' : 'Cart'} arrow>
              <IconButton onClick={() => setCartOpen(true)} sx={iconBtnSx}>
                <Badge
                  badgeContent={cartCount}
                  sx={{
                    '& .MuiBadge-badge': {
                      background: `linear-gradient(135deg, ${goldAccent}, #FFD700)`,
                      color: '#111',
                      fontWeight: 700,
                      fontSize: '0.65rem',
                      height: 17,
                      minWidth: 17,
                      border: '2px solid #141414',
                    },
                  }}
                >
                  <ShoppingCart fontSize="small" />
                </Badge>
              </IconButton>
            </Tooltip>

            {/* Share - mobile / tablet only */}
            {(isMobile || isTablet) && (
              <Tooltip title={language === 'ar' ? 'مشاركة' : 'Share'} arrow>
                <IconButton onClick={shareWithWebAPI} sx={iconBtnSx}>
                  <ShareIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {user ? (
              <>
                {/* Pharmacy status (admin only, non-mobile) */}
                {user.role === 'admin' && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mr: 0.5,
                      px: 1,
                      py: 0.25,
                      borderRadius: '999px',
                      border: '1px solid rgba(230, 184, 0, 0.25)',
                      background: 'rgba(255, 255, 255, 0.02)',
                    }}
                  >
                    {loadingStatus ? (
                      <CircularProgress size={18} sx={{ color: goldAccent }} />
                    ) : (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={pharmacyStatus}
                            onChange={handleStatusChange}
                            disabled={loadingStatus}
                            size="small"
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: goldAccent,
                                '&:hover': { backgroundColor: softGold },
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: goldAccent,
                                opacity: 0.6,
                              },
                              '& .MuiSwitch-track': { backgroundColor: '#3A3A3A' },
                            }}
                          />
                        }
                        label={
                          <Typography
                            variant="caption"
                            sx={{
                              color: pharmacyStatus ? goldAccent : '#B8B8B8',
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              letterSpacing: 0.3,
                            }}
                          >
                            {pharmacyStatus ? openTxt : closedTxt}
                          </Typography>
                        }
                        labelPlacement="start"
                        sx={{ mr: 0 }}
                      />
                    )}
                  </Box>
                )}

                {/* Notifications */}
                <Tooltip title={language === 'ar' ? 'الإشعارات' : 'Notifications'} arrow>
                  <IconButton ref={notificationRef} onClick={handleNotificationClick} sx={iconBtnSx}>
                    <Badge
                      badgeContent={unreadCount}
                      sx={{
                        '& .MuiBadge-badge': {
                          background: `linear-gradient(135deg, ${goldAccent}, #FFD700)`,
                          color: '#111',
                          fontWeight: 700,
                          fontSize: '0.65rem',
                          height: 17,
                          minWidth: 17,
                          border: '2px solid #141414',
                        },
                      }}
                    >
                      {unreadCount > 0 ? <NotificationsIcon fontSize="small" /> : <NotificationsNone fontSize="small" />}
                    </Badge>
                  </IconButton>
                </Tooltip>

                {/* Language toggle — pill style */}
                <Button
                  onClick={toggleLanguage}
                  sx={{
                    minWidth: 'auto',
                    minHeight: 32,
                    height: 32,
                    px: { xs: 1.2, sm: 1.5 },
                    borderRadius: '999px',
                    color: '#EDEDED',
                    fontWeight: 700,
                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                    letterSpacing: 0.5,
                    border: '1px solid rgba(255,255,255,0.12)',
                    background: 'rgba(255,255,255,0.03)',
                    '&:hover': {
                      color: goldAccent,
                      borderColor: 'rgba(230, 184, 0, 0.45)',
                      background: softGold,
                    },
                  }}
                >
                  {language === 'ar' ? 'EN' : 'AR'}
                </Button>

                {/* Logout */}
                <Tooltip title={language === 'ar' ? 'تسجيل الخروج' : 'Logout'} arrow>
                  <IconButton onClick={handleLogout} sx={iconBtnSx}>
                    <Logout fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <>
                <Button
                  component={Link}
                  to="/login"
                  sx={{
                    color: '#EDEDED',
                    fontWeight: 600,
                    fontSize: { xs: '0.72rem', sm: '0.8rem' },
                    borderRadius: '999px',
                    px: { xs: 1.2, sm: 1.5 },
                    '&:hover': { color: goldAccent, background: softGold },
                  }}
                >
                  {language === 'ar' ? keywordsData.login_ar : keywordsData.login_en}
                </Button>
                <Button
                  component={Link}
                  to="/register"
                  sx={{
                    color: '#111',
                    fontWeight: 700,
                    fontSize: { xs: '0.72rem', sm: '0.8rem' },
                    borderRadius: '999px',
                    px: { xs: 1.5, sm: 2 },
                    background: `linear-gradient(135deg, ${goldAccent}, #FFD700)`,
                    boxShadow: '0 4px 14px rgba(230, 184, 0, 0.25)',
                    '&:hover': {
                      background: `linear-gradient(135deg, #FFD700, ${goldAccent})`,
                      boxShadow: '0 6px 20px rgba(230, 184, 0, 0.4)',
                      transform: 'translateY(-1px)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  {language === 'ar' ? keywordsData.register_ar : keywordsData.register_en}
                </Button>
                <Button
                  onClick={toggleLanguage}
                  sx={{
                    minWidth: 'auto',
                    minHeight: 32,
                    height: 32,
                    px: { xs: 1.2, sm: 1.5 },
                    borderRadius: '999px',
                    color: '#EDEDED',
                    fontWeight: 700,
                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                    border: '1px solid rgba(255,255,255,0.12)',
                    background: 'rgba(255,255,255,0.03)',
                    '&:hover': {
                      color: goldAccent,
                      borderColor: 'rgba(230, 184, 0, 0.45)',
                      background: softGold,
                    },
                  }}
                >
                  {language === 'ar' ? 'EN' : 'AR'}
                </Button>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Notifications Popover */}
      <Popper
        id={id}
        dir={language === 'ar' ? 'rtl' : 'ltr'}
        open={open}
        anchorEl={anchorEl}
        placement="bottom-end"
        sx={{ zIndex: 1400 }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper
            elevation={0}
            sx={{
              width: { xs: '92vw', sm: 400 },
              maxHeight: 480,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              background: 'rgba(20, 20, 20, 0.95)',
              backdropFilter: 'blur(20px) saturate(180%)',
              WebkitBackdropFilter: 'blur(20px) saturate(180%)',
              border: '1px solid rgba(230, 184, 0, 0.25)',
              borderRadius: '16px',
              boxShadow: '0 20px 50px rgba(0,0,0,0.5), 0 0 0 1px rgba(230,184,0,0.08)',
              mt: 1.5,
              color: '#fff',
            }}
          >
            {/* Header */}
            <Box
              sx={{
                p: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid rgba(255,255,255,0.06)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem' }}>
                  {language === 'ar' ? keywordsData.noty_title_ar : keywordsData.noty_title_en}
                </Typography>
                {unreadCount > 0 && (
                  <Box
                    sx={{
                      px: 0.9,
                      py: 0.15,
                      borderRadius: '999px',
                      background: softGold,
                      color: goldAccent,
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      border: `1px solid rgba(230,184,0,0.3)`,
                    }}
                  >
                    {unreadCount}
                  </Box>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 0.25 }}>
                <IconButton
                  size="small"
                  onClick={markAllAsRead}
                  disabled={unreadCount === 0}
                  sx={{ color: '#B8B8B8', '&:hover': { color: goldAccent, background: softGold } }}
                >
                  <DoneAll fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={toggleSound}
                  sx={{ color: soundEnabled ? goldAccent : '#B8B8B8', '&:hover': { background: softGold } }}
                >
                  {soundEnabled ? <VolumeUp fontSize="small" /> : <VolumeOff fontSize="small" />}
                </IconButton>
                <IconButton
                  size="small"
                  onClick={handleClose}
                  sx={{ color: '#B8B8B8', '&:hover': { color: goldAccent, background: softGold } }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {/* Body */}
            <Box sx={{ overflowY: 'auto', flex: 1 }}>
              {loading ? (
                <Box sx={{ p: 5, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress size={28} sx={{ color: goldAccent }} />
                </Box>
              ) : notifications.length === 0 ? (
                <Box sx={{ p: 5, textAlign: 'center' }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      background: softGold,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 1.5,
                    }}
                  >
                    <NotificationsNone sx={{ color: goldAccent }} />
                  </Box>
                  <Typography sx={{ color: '#B8B8B8', fontSize: '0.85rem' }}>
                    {language === 'ar' ? keywordsData.noty_no_record_ar : keywordsData.noty_no_record_en}
                  </Typography>
                </Box>
              ) : (
                <List dense sx={{ p: 0 }}>
                  {notifications.map((notification) => (
                    <ListItem
                      key={notification.id}
                      sx={{
                        background: notification.read ? 'transparent' : 'rgba(230, 184, 0, 0.05)',
                        cursor: 'pointer',
                        borderBottom: '1px solid rgba(255,255,255,0.04)',
                        transition: 'background 0.2s ease',
                        '&:hover': { background: 'rgba(230, 184, 0, 0.12)' },
                        py: 1.5,
                        px: 2,
                        alignItems: 'flex-start',
                      }}
                      onClick={() => {
                        markAsRead(notification.id);
                        if (notification.link) navigate(notification.link);
                        handleClose();
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36, mt: 0.25 }}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '10px',
                            background: softGold,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {notification.icon === 'order' && <ShoppingCart sx={{ color: goldAccent, fontSize: 18 }} />}
                          {notification.icon === 'system' && <AdminPanelSettings sx={{ color: goldAccent, fontSize: 18 }} />}
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={notification.title}
                        secondary={notification.message}
                        primaryTypographyProps={{
                          fontWeight: notification.read ? 500 : 700,
                          color: notification.read ? '#B8B8B8' : '#fff',
                          fontSize: '0.85rem',
                          mb: 0.25,
                        }}
                        secondaryTypographyProps={{
                          color: '#7A7A7A',
                          fontSize: '0.74rem',
                          sx: { lineHeight: 1.4 },
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ color: '#6A6A6A', minWidth: 52, textAlign: 'right', fontSize: '0.68rem', mt: 0.25 }}
                      >
                        {new Date(notification.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>

      {/* Floating Bottom Navigation */}
      <Box
        sx={{
          position: 'fixed',
          bottom: { xs: 8, sm: 12, md: 16 },
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1200,
          width: { xs: 'calc(100% - 16px)', sm: 'auto' },
          maxWidth: '100%',
          pointerEvents: 'none',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            pointerEvents: 'auto',
            background: 'rgba(20, 20, 20, 0.9)',
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
            border: '1px solid rgba(230, 184, 0, 0.2)',
            borderRadius: '20px',
            boxShadow: '0 12px 40px rgba(0,0,0,0.5), 0 0 0 1px rgba(230,184,0,0.05)',
            overflow: 'hidden',
          }}
        >
          <BottomNavigation
            value={value}
            onChange={(event, newValue) => {
              setValue(newValue);
              navigate(`/${newValue === 'home' ? '' : newValue}`);
            }}
            showLabels
            sx={{
              background: 'transparent',
              height: { xs: 58, sm: 62, md: 66 },
              width: '100%',
              px: 0.5,
            }}
          >
            {getBottomNavItems().map((item) => (
              <BottomNavigationAction
                key={item.value}
                label={item.label}
                value={item.value}
                icon={item.icon}
                sx={{
                  color: '#8A8A8A',
                  minWidth: { xs: 60, sm: 72, md: 84 },
                  mx: 0.25,
                  borderRadius: '14px',
                  transition: 'all 0.2s ease',
                  '& .MuiBottomNavigationAction-label': {
                    fontSize: { xs: '0.62rem', sm: '0.68rem', md: '0.72rem' },
                    fontWeight: 500,
                    mt: 0.25,
                  },
                  '& .MuiSvgIcon-root': {
                    fontSize: { xs: '1.35rem', sm: '1.45rem', md: '1.5rem' },
                  },
                  '&.Mui-selected': {
                    color: goldAccent,
                    background: 'rgba(230, 184, 0, 0.1)',
                    '& .MuiBottomNavigationAction-label': {
                      fontWeight: 700,
                      color: goldAccent,
                    },
                  },
                  '&:hover': {
                    background: 'rgba(255,255,255,0.04)',
                  },
                }}
              />
            ))}
          </BottomNavigation>
        </Paper>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            background: 'rgba(20, 20, 20, 0.95)',
            backdropFilter: 'blur(20px)',
            color: '#fff',
            border: '1px solid rgba(230, 184, 0, 0.3)',
            borderRadius: '14px',
            fontWeight: 600,
            boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
            '& .MuiAlert-icon': {
              color: snackbar.severity === 'success' ? goldAccent : '#FF5252',
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default NavBar;